import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import Select, { MultiValue, components } from "react-select";
import { AsyncPaginate, LoadOptions } from "react-select-async-paginate";
import { Search, FileText, Download } from "lucide-react";
import { showError, showSuccess } from "toast/toast";
import { useNavigate } from "react-router-dom";

// ===== Types =====
import { getAuthCookie } from "../../utils/authCookies";
import { appendPermissionsFalse } from "api/salesOrderApi";

type PayerScope = "company" | "office" | "agent" | "group";
type BrokerageFirm = { id: string; name: string };
type Office = { id: string; label: string };
type Agent = { id: string; name: string };
type Group = { id: string; name: string };
type TeamMember = { id: string; name: string; firstName: string; lastName: string };

// Removed FirmItem type - now using Select2 format directly

type BrokerageOption = { 
  value: string; 
  label: string; 
  raw: { id?: string; text: string; organization_id?: string; _id?: string }
};

type TeamMemberOption = { 
  value: string; 
  label: string; 
  raw: { id: string; name: string } // Select2 format for team members
};

type OfficeOption = { 
  value: string; 
  label: string; 
  raw: { id: string; name: string } // Select2 format for offices
};

type AgentOption = { 
  value: string; 
  label: string; 
  raw: { id: string; name: string } // Select2 format for agents
};

// ===== React Select Components =====
const DropdownIndicator = (props: any) => {
  if (props.selectProps.isDisabled) return null;
  return (
    <components.DropdownIndicator {...props}>
      <i className="ri-arrow-down-s-line fs-5" style={{ color: "#6c757d" }} />
    </components.DropdownIndicator>
  );
};

const customSelectStyles = {
  indicatorsContainer: (p: any, s: any) => ({ ...p, display: s.isDisabled ? "none" : "flex" }),
  dropdownIndicator: (p: any, s: any) => ({ ...p, display: s.isDisabled ? "none" : "flex" }),
  indicatorSeparator: () => ({ display: "none" }),
  control: (p: any, s: any) => ({
    ...p,
    backgroundColor: s.isDisabled ? "#f2f2f2" : p.backgroundColor,
    cursor: s.isDisabled ? "not-allowed" : "pointer",
    opacity: 1,
    borderWidth: 2,
    borderColor: "#c9ced6",
    minHeight: 44,
    borderRadius: 8,
  }),
  singleValue: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color }),
  valueContainer: (p: any) => ({ ...p, padding: "6px 10px" }),
  input: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color, WebkitTextFillColor: s.isDisabled ? "#000" : undefined }),
  placeholder: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color }),
  multiValue: (p: any, s: any) => ({ ...p, backgroundColor: s.isDisabled ? "#e5e7eb" : p.backgroundColor }),
  multiValueLabel: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color }),
  multiValueRemove: (p: any, s: any) => ({ ...p, display: s.isDisabled ? "none" : p.display }),
};

interface PayerScopeSectionProps {
  onOrdersFetched?: (data: {
    orders: any[];
    total: number;
    totalPages: number;
    page: number;
    limit: number;
  }) => void;
  onScopeDataChange?: (scopeData: {
    scope: string;
    company: string;
    teamMembers: string[];
    offices: string[];
    agents: string[];
    bill_from: string;
    bill_to: string;
  }) => void;
  baseRoute?: string;
  pagination?: { pageIndex: number; pageSize: number };
  onPaginationReset?: () => void;
  /** Selected orders from PaymentOrders — sent only when non-empty */
  selectedOrdersForReport?: Array<{ order_id: string; order_type: string }>;
}

export default function PayerScopeSection({ onOrdersFetched, onScopeDataChange, baseRoute = '/signage', pagination: externalPagination, onPaginationReset, selectedOrdersForReport = [] }: PayerScopeSectionProps) {
  // Auth placeholders (replace with real context)
  const token = getAuthCookie("sessionToken");
  const organizationId = getAuthCookie("organizationId");
  const membershipId = getAuthCookie("membershipId");
  const navigate = useNavigate();

  // State
  const [scope, setScope] = useState<PayerScope>("company");
  // Removed firms state - now using AsyncPaginate
  const [brokerage, setBrokerage] = useState<BrokerageFirm | null>(null);
  const [selectedBrokerage, setSelectedBrokerage] = useState<BrokerageOption | null>(null);
  const [offices, setOffices] = useState<Office[]>([]);
  const [selectedOffices, setSelectedOffices] = useState<Office[]>([]);
  const [selectedOffice, setSelectedOffice] = useState<any>(null);
  const [selectedOfficeOption, setSelectedOfficeOption] = useState<OfficeOption | null>(null);

  const [agents, setAgents] = useState<Agent[]>([]);
  const [selectedAgents, setSelectedAgents] = useState<Agent[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<any>(null);
  const [selectedAgentOption, setSelectedAgentOption] = useState<AgentOption | null>(null);

  const [groups, setGroups] = useState<Group[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);

  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [selectedTeamMembers, setSelectedTeamMembers] = useState<TeamMember[]>([]);
  const [selectedTeamMember, setSelectedTeamMember] = useState<any>(null);
  const [selectedTeamMemberOption, setSelectedTeamMemberOption] = useState<TeamMemberOption | null>(null);

  const [billToCompany, setBillToCompany] = useState(true);
  const [placedBy, setPlacedBy] = useState({ officeManager: true, agent: true });
  const [orderTypes, setOrderTypes] = useState({ sales: true, service_install: true, removal: true });
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [statusUnpaid, setStatusUnpaid] = useState(true);
  const [statusPartial, setStatusPartial] = useState(true);
  const [statusOverdue, setStatusOverdue] = useState(true);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetchingOrders, setFetchingOrders] = useState(false);
  const [ordersFetched, setOrdersFetched] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [vendorName, setVendorName] = useState<string>("");

  // Removed fetchBrokerageFirms - now using AsyncPaginate

  // ===== API: Paginated Brokerage firms =====
  const PAGE_SIZE = 10;
  const normalizeBrokerageOrgId = (item: any) =>
    String(item?.organization_id ?? item?.id ?? item?._id ?? "");
  
  const loadBrokerageOptions: LoadOptions<BrokerageOption, any, { page: number }> = useCallback(
    async (inputValue, _loadedOptions, additional) => {
      const page = additional?.page ?? 1;

      try {
        // Build URL with pagination and search
        const params = new URLSearchParams({ 
          page: String(page),
          limit: String(PAGE_SIZE)
        });
        const q = (inputValue || "").trim();
        if (q) params.set("q", q);

        const url = `${process.env.REACT_APP_API_URL}/created_and_linked_brokerage_firms/select2?${params.toString()}`;
        console.log('Loading brokerage options:', { page, q, url });
        
        const response = await fetch(url, {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          console.error("Failed to fetch brokerage firms:", response.statusText);
          return { options: [], hasMore: false, additional: { page } };
        }

        const responseData = await response.json();
        console.log('Brokerage API response:', { page, q, responseData });

        // Handle Select2 response format
        const results = responseData.results || [];
        const hasMore = responseData.pagination?.more || false;

        // Map to react-select options using Select2 format
        const options: BrokerageOption[] = results.map((item: any) => ({
          value: normalizeBrokerageOrgId(item),
          label: item.text,
          raw: item, // Store the original Select2 item
        }));

        return {
          options,
          hasMore,
          additional: { page: page + 1 },
        };
      } catch (error) {
        console.error("Error loading brokerage options:", error);
        return { options: [], hasMore: false, additional: { page } };
      }
    },
    [token]
  );

  // ===== API: Team Members with Select2 =====
  // ===== API: Offices for selected brokerage (Select2 style) =====
  const loadOfficeOptions: LoadOptions<OfficeOption, any, { page: number }> = useCallback(
    async (inputValue, _loadedOptions, additional) => {
      const page = additional?.page ?? 1;

      // Guard: without a selected brokerage, return empty
      const brokerageId =
        selectedBrokerage?.raw?.organization_id ||
        selectedBrokerage?.value;
      if (!brokerageId) return { options: [], hasMore: false, additional: { page } };

      try {
        const q = (inputValue || "").trim();
        const url = `${process.env.REACT_APP_API_URL}${appendPermissionsFalse(`/organizations/${brokerageId}/offices/select`)}`;
        const response = await fetch(url, {
          method: "POST",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            page,
            limit: PAGE_SIZE,
            search: q,
          }),
        });
        if (!response.ok) return { options: [], hasMore: false, additional: { page } };
        const json = await response.json();
        const list = json?.results || [];
        const options: OfficeOption[] = list.map((office: any) => ({
          value: office.id || office._id,
          label: office.officeName || office.name || "---",
          raw: office,
        }));
        // Add "All" option at the top only on first page and when not searching
        const allOptions: OfficeOption[] = page === 1 && !q ? [{
          value: "__ALL__",
          label: "All",
          raw: { id: "__ALL__", name: "All" }
        }] : [];
        const hasMore = json?.pagination?.more ?? false;
        return { options: [...allOptions, ...options], hasMore, additional: { page: page + 1 } };
      } catch (err) {
        console.error("Failed to load office options:", err);
        return { options: [], hasMore: false, additional: { page } };
      }
    },
    [selectedBrokerage?.value, token]
  );

  /** GET /offices/members/select2 — server filters by officeIDs[] and search q; paginates with page/limit. */
  const fetchOfficeTeamMembersSelect2Page = useCallback(
    async (officeIds: string[], page: number, q: string) => {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(PAGE_SIZE),
      });
      if (q) params.set("q", q);
      officeIds.forEach((id) => params.append("officeIDs[]", id));
      const url = `${process.env.REACT_APP_API_URL}/offices/members/select2?${params.toString()}`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        return { results: [] as any[], hasMore: false };
      }
      const json = await response.json();
      const data = json?.data ?? json;
      const results = Array.isArray(data?.results) ? data.results : [];
      const hasMore = Boolean(data?.pagination?.more ?? false);
      return { results, hasMore };
    },
    [token]
  );

  // ===== API: Team Members for selected office (Select2 style) =====
  const loadTeamMemberOptions: LoadOptions<TeamMemberOption, any, { page: number }> = useCallback(
    async (inputValue, _loadedOptions, additional) => {
      const page = additional?.page ?? 1;
      if (selectedOffices.length === 0) return { options: [], hasMore: false, additional: { page } };

      const officeIds = selectedOffices.map((o) => o.id).filter((id) => id && id !== "__ALL__");
      if (officeIds.length === 0) return { options: [], hasMore: false, additional: { page } };

      try {
        const q = (inputValue || "").trim();
        const { results, hasMore } = await fetchOfficeTeamMembersSelect2Page(officeIds, page, q);
        const options: TeamMemberOption[] = results.map((u: any) => {
          const id = String(u?.id ?? u?._id ?? "");
          const label = String(u?.text ?? u?.name ?? "").trim() || "---";
          return {
            value: id,
            label,
            raw: { id, name: label },
          };
        });
        const allOptions: TeamMemberOption[] = page === 1 && !q ? [{
          value: "__ALL__",
          label: "All",
          raw: { id: "__ALL__", name: "All" }
        }] : [];
        return { options: [...allOptions, ...options], hasMore, additional: { page: page + 1 } };
      } catch (err) {
        console.error("Failed to load team member options:", err);
        return { options: [], hasMore: false, additional: { page } };
      }
    },
    [selectedOffices, fetchOfficeTeamMembersSelect2Page]
  );

  // ===== API: Offices with Select2 =====
  // const loadOfficeOptions: LoadOptions<OfficeOption, any, { page: number }> = useCallback(
  //   async (inputValue, _loadedOptions, additional) => {
  //     const page = additional?.page ?? 1;

  //     try {
  //       // Get selected team member IDs from selectedTeamMembers
  //       const teamMemberIds = selectedTeamMembers.map(tm => tm.id);
        
  //       if (teamMemberIds.length === 0) {
  //         return { options: [], hasMore: false };
  //       }

  //       // Build URL with pagination and search
  //       const params = new URLSearchParams({ 
  //         page: String(page),
  //         limit: String(PAGE_SIZE)
  //       });
        
  //       // Add team member IDs as array
  //       teamMemberIds.forEach(id => {
  //         params.append('teamMemberIDs[]', id);
  //       });
        
  //       const q = (inputValue || "").trim();
  //       if (q) params.set("q", q);

  //       const url = `${process.env.REACT_APP_API_URL}/users/brokerage-team-members/offices/select2?${params.toString()}`;
  //       console.log('Loading office options:', { page, q, teamMemberIds, url });
        
  //       const response = await fetch(url, {
  //         headers: {
  //           Accept: "application/json",
  //           Authorization: `Bearer ${token}`,
  //         },
  //       });

  //       if (!response.ok) {
  //         throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  //       }

  //       const data = await response.json();
  //       console.log('Office options response:', data);

  //       const options: OfficeOption[] = (data.results || []).map((item: any) => ({
  //         value: item.id || item._id,
  //         label: item.name,
  //         raw: item
  //       }));

  //       // Add "All" option at the top only on first page and when not searching
  //       const allOptions: OfficeOption[] = page === 1 && !q ? [{
  //         value: "__ALL__",
  //         label: "All",
  //         raw: { id: "__ALL__", name: "All" }
  //       }] : [];

  //       return {
  //         options: [...allOptions, ...options],
  //         hasMore: data.pagination?.more || false,
  //         additional: { page: page + 1 }
  //       };
  //     } catch (err) {
  //       console.error("Failed to load office options:", err);
  //       return { options: [], hasMore: false };
  //     }
  //   },
  //   [selectedTeamMembers, token]
  // );

  // ===== API: Agents with Select2 (for company/office scope - by office IDs) =====
  const loadAgentOptions: LoadOptions<AgentOption, any, { page: number }> = useCallback(
    async (inputValue, _loadedOptions, additional) => {
      const page = additional?.page ?? 1;

      try {
        // Get selected office IDs from selectedOffices
        const officeIds = selectedOffices.map(office => office.id);
        
        if (officeIds.length === 0) {
          return { options: [], hasMore: false };
        }

        // Build URL with pagination and search
        const params = new URLSearchParams({ 
          page: String(page),
          limit: String(PAGE_SIZE)
        });
        
        // Add office IDs as array
        officeIds.forEach(id => {
          params.append('officeIDs[]', id);
        });
        
        const q = (inputValue || "").trim();
        if (q) params.set("q", q);

        const url = `${process.env.REACT_APP_API_URL}/organization-memberships/brokerage-agents/by-office-ids/select2?${params.toString()}`;
        console.log('Loading agent options:', { page, q, officeIds, url });
        
        const response = await fetch(url, {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        console.log('Agent options response:', data);

        const options: AgentOption[] = (data.results || []).map((item: any) => ({
          value: item.id || item._id,
          label: item.name,
          raw: item
        }));

        // Add "All" option at the top only on first page and when not searching
        const allOptions: AgentOption[] = page === 1 && !q ? [{
          value: "__ALL__",
          label: "All",
          raw: { id: "__ALL__", name: "All" }
        }] : [];

        return {
          options: [...allOptions, ...options],
          hasMore: data.pagination?.more || false,
          additional: { page: page + 1 }
        };
      } catch (err) {
        console.error("Failed to load agent options:", err);
        return { options: [], hasMore: false };
      }
    },
    [selectedOffices, token]
  );

  // ===== API: Signage Agents with Select2 (for agent scope - all signage agents) =====
  const loadSignageAgentOptions: LoadOptions<AgentOption, any, { page: number }> = useCallback(
    async (inputValue, _loadedOptions, additional) => {
      const page = additional?.page ?? 1;
      const q = (inputValue || "").trim();

      try {
        if (!organizationId) {
          return { options: [], hasMore: false, additional: { page: 1 } };
        }

        const url =
          `${process.env.REACT_APP_API_URL}/organizations/${organizationId}/agents/select2` +
          `?page=${page}&limit=${PAGE_SIZE}` +
          (q ? `&search=${encodeURIComponent(q)}` : "");

        console.log('Loading signage agent options:', { page, q, organizationId, url });

        const response = await fetch(url, {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          console.error("Failed to fetch signage agents:", response.statusText);
          return { options: [], hasMore: false, additional: { page } };
        }

        const json = await response.json();
        console.log('Signage agent options response:', json);

        const list = Array.isArray(json) ? json : json.data ?? json.results ?? [];

        const options: AgentOption[] = list.map((u: any) => ({
          value: u.id || u._id || "",
          label: u?.name || `${u?.firstName || ""} ${u?.lastName || ""}`.trim() || "(no name)",
          raw: u
        }));

        const hasMore = Array.isArray(json)
          ? options.length === PAGE_SIZE
          : json?.pagination?.more ??
            (json?.total ? page * (json?.limit ?? PAGE_SIZE) < json.total : options.length === PAGE_SIZE);

        // Add "All" option at the top only on first page and when not searching
        const allOptions: AgentOption[] = page === 1 && !q ? [{
          value: "__ALL__",
          label: "All",
          raw: { id: "__ALL__", name: "All" }
        }] : [];

        return {
          options: [...allOptions, ...options],
          hasMore,
          additional: { page: page + 1 },
        };
      } catch (err) {
        console.error("Failed to load signage agent options:", err);
        return { options: [], hasMore: false, additional: { page } };
      }
    },
    [organizationId, token]
  );

  const fetchAgents = async () => {
    try {
      setLoading(true);

      let response;

      if (scope === "agent") {
        // Direct agent call for agent scope
        response = await fetch(
          `${process.env.REACT_APP_API_URL}/organizations/${organizationId}/agents`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
      } else {
        // Office-based agent call for company/office scope
        if (selectedOffices.length === 0) {
          setAgents([]);
          return;
        }

        response = await fetch(
          `${process.env.REACT_APP_API_URL}/agents/offices`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              office_ids: selectedOffices.map(o => o.id)
            })
          }
        );
      }

      if (!response.ok) {
        throw new Error("Failed to fetch agents");
      }

      const data = await response.json();
      const agentList: Agent[] = (Array.isArray(data) ? data : []).map((agent: any) => ({
        id: agent.id || agent._id,
        name: `${agent.firstName || ""} ${agent.lastName || ""}`.trim(),
      }));
      setAgents(agentList);
    } catch (err) {
      showError(err instanceof Error ? err.message : "Failed to fetch agents");
    } finally {
      setLoading(false);
    }
  };

  const fetchGroups = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/customer-groups`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch groups");
      }

      const data = await response.json();
      const groupList: Group[] = (Array.isArray(data) ? data : []).map((group: any) => ({
        id: group._id || group.id,
        name: group.name || "",
      }));
      setGroups(groupList);
    } catch (err) {
      showError(err instanceof Error ? err.message : "Failed to fetch groups");
    } finally {
      setLoading(false);
    }
  };

  // ===== API: Offices by Brokerage =====
  // const fetchOffices = async (brokerageId: string) => {
  //   try {
  //     setLoading(true);
  //     const base = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
  //     const resp = await fetch(`${base}/office/${brokerageId}`, { headers: { Authorization: `Bearer ${token}` } });
  //     const data = await resp.json();
  //     if (!resp.ok) showError(data?.error || "Failed to fetch offices");
  //     const list: Office[] = (data || []).map((o: any) => ({ id: o._id, label: o.officeName }));
  //     setOffices(list);
  //   } catch (e: any) {
  //     showError(e?.message || "Failed to fetch offices");
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // ===== API: Marketplace Profiles =====
  const fetchMarketplaceProfiles = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/marketplace-profiles/organizations/${organizationId}?permissions=false`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch marketplace profiles");
      }

      const data = await response.json();
      console.log("Marketplace profiles data:", data);
      
      // Get the first profile's vendor_name
      if (Array.isArray(data) && data.length > 0) {
        const vendorName = data[0].vendor_name || "";
        setVendorName(vendorName);
      }
    } catch (err) {
      console.error("Error fetching marketplace profiles:", err);
      // Don't show error to user as this is not critical
    }
  };

  // Removed fetchBrokerageFirms useEffect - now using AsyncPaginate

  // Fetch marketplace profiles on component mount
  useEffect(() => {
    if (token) {
      fetchMarketplaceProfiles();
    }
  }, [token]);

  // Fetch agents when scope changes or when offices are selected
  // Note: For agent scope, we use AsyncPaginate so no need to call fetchAgents
  useEffect(() => {
    if (scope === "agent") {
      // Agent scope uses AsyncPaginate, no need to fetch here
      // Just clear the agents state since we'll use chips for selected agents
      setAgents([]);
    } else if ((scope === "company" || scope === "office") && selectedOffices.length > 0) {
      // Office-based agent call for company/office scope
      fetchAgents();
    } else if (scope === "company" || scope === "office") {
      // Clear agents if no offices selected
      setAgents([]);
    }
  }, [scope, selectedOffices]);

  // Fetch groups when scope changes to "group"
  useEffect(() => {
    if (scope === "group") {
      fetchGroups();
    }
  }, [scope]);

  // useEffect(() => {
  //   setSelectedOffices([]);
  //   if (brokerage?.id) fetchOffices(brokerage.id);
  //   else setOffices([]);
  // }, [brokerage?.id]);

  // Clear team members when brokerage changes (team members are now loaded via AsyncPaginate)
  useEffect(() => {
    setSelectedTeamMembers([]);
    setSelectedTeamMemberOption(null);
  }, [selectedBrokerage]);

  // Handler for scope change - clears all selections
  const handleScopeChange = (newScope: PayerScope) => {
    setScope(newScope);
    // Clear all selections when scope changes
    setBrokerage(null);
    setSelectedBrokerage(null);
    setSelectedTeamMembers([]);
    setSelectedTeamMemberOption(null);
    setSelectedOffices([]);
    setSelectedOfficeOption(null);
    setSelectedAgents([]);
    setSelectedAgentOption(null);
    setSelectedGroup(null);
    setSelectedAgent(null);
    setSelectedOffice(null);
    // Clear orders fetched flag to hide payment components
    setOrdersFetched(false);
    // Notify parent to clear orders data
    if (onOrdersFetched) {
      onOrdersFetched({ orders: [], total: 0, totalPages: 0, page: 1, limit: 10 });
    }
  };

  // Notify parent component when scope data changes
  useEffect(() => {
    if (onScopeDataChange) {
      const scopeData = {
        scope: scope,
        company: selectedBrokerage?.label || "",
        teamMembers: selectedTeamMembers.map(tm => tm.name),
        offices: selectedOffices.map(o => o.label),
        agents: selectedAgents.map(a => a.name),
        bill_from: vendorName || "",
        bill_to: selectedBrokerage?.label || ""
      };
      onScopeDataChange(scopeData);
    }
  }, [scope, selectedBrokerage, selectedTeamMembers, selectedOffices, selectedAgents, vendorName, onScopeDataChange]);

  // Helper function to fetch all offices
  const fetchAllOffices = async () => {
    try {
      const allOffices: Office[] = [];
      let page = 1;
      let hasMore = true;

      // If team members are selected, use that endpoint (original flow).
      if (selectedTeamMembers.length > 0) {
        while (hasMore) {
          const params = new URLSearchParams({
            page: String(page),
            limit: String(PAGE_SIZE)
          });
          selectedTeamMembers.forEach(tm => {
            params.append('membershipIDs[]', tm.id);
          });
          const url = `${process.env.REACT_APP_API_URL}/organization-memberships/brokerage-team-members/offices/select2?${params.toString()}`;
          const response = await fetch(url, {
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${token}`,
            },
          });
          if (!response.ok) break;
          const data = await response.json();
          const results = data.results || [];
          results.forEach((item: any) => {
            allOffices.push({
              id: item.id || item._id,
              label: item.name
            });
          });
          hasMore = data.pagination?.more || false;
          page++;
        }
      } else if (selectedBrokerage?.value) {
        // Fallback: fetch all offices for the selected brokerage
        try {
          const url = `${process.env.REACT_APP_API_URL}${appendPermissionsFalse(`/organizations/${selectedBrokerage.value}/offices/select`)}`;
          const resp = await fetch(url, {
            method: "POST",
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ page: 1, limit: 1000, search: "" }),
          });
          if (resp.ok) {
            const json = await resp.json();
            const list = json?.results || [];
            list.forEach((o: any) => {
              allOffices.push({ id: o.id || o._id, label: o.officeName || o.name || o.officeName });
            });
          }
        } catch (e) {
          console.error("Failed to fetch offices for brokerage:", e);
        }
      }

      setSelectedOffices(allOffices);
    } catch (err) {
      console.error("Failed to fetch all offices:", err);
    }
  };

  // ===== Handlers for adding offices and agents =====
  const handleOfficeChange = (opt: OfficeOption | null) => {
    if (opt?.value) {
      if (opt.value === "__ALL__") {
        // Fetch and select all offices
        fetchAllOffices();
      } else {
        const office = { id: opt.value, label: opt.label };
        if (!selectedOffices.some(o => o.id === office.id)) {
          setSelectedOffices([...selectedOffices, office]);
        }
      }
    }
    // Clear the select after adding
    setSelectedOfficeOption(null);
  };

  // Helper function to fetch all agents
  const fetchAllAgents = async () => {
    try {
      const allAgents: Agent[] = [];
      
      if (scope === "agent") {
        // For agent scope, fetch all signage agents using select2 endpoint with pagination
        if (!organizationId) return;

        let page = 1;
        let hasMore = true;

        while (hasMore) {
          const url =
            `${process.env.REACT_APP_API_URL}/organizations/${organizationId}/agents/select2` +
            `?page=${page}&limit=${PAGE_SIZE}`;

          const response = await fetch(url, {
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${token}`,
            },
          });

          if (!response.ok) break;

          const json = await response.json();
          const list = Array.isArray(json) ? json : json.data ?? json.results ?? [];

          list.forEach((u: any) => {
            allAgents.push({
              id: u.id || u._id || "",
              name: u?.name || `${u?.firstName || ""} ${u?.lastName || ""}`.trim() || "(no name)",
            });
          });

          hasMore = Array.isArray(json)
            ? list.length === PAGE_SIZE
            : json?.pagination?.more ?? false;
          page++;
        }

        setSelectedAgents(allAgents);
      } else {
        // For company/office scope, fetch by offices
        let page = 1;
        let hasMore = true;

        while (hasMore) {
          const params = new URLSearchParams({ 
            page: String(page),
            limit: String(PAGE_SIZE)
          });
          
          selectedOffices.forEach(office => {
            params.append('officeIDs[]', office.id);
          });

          const url = `${process.env.REACT_APP_API_URL}/organization-memberships/brokerage-agents/by-office-ids/select2?${params.toString()}`;
          const response = await fetch(url, {
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${token}`,
            },
          });

          if (!response.ok) break;

          const data = await response.json();
          const results = data.results || [];
          
          results.forEach((item: any) => {
            allAgents.push({
              id: item.id || item._id,
              name: item.name
            });
          });

          hasMore = data.pagination?.more || false;
          page++;
        }

        setSelectedAgents(allAgents);
      }
    } catch (err) {
      console.error("Failed to fetch all agents:", err);
    }
  };

  const handleAgentChange = (opt: AgentOption | null) => {
    if (opt?.value) {
      if (opt.value === "__ALL__") {
        // Fetch and select all agents
        fetchAllAgents();
      } else {
        const agent = { id: opt.value, name: opt.label };
        if (!selectedAgents.some(a => a.id === agent.id)) {
          setSelectedAgents([...selectedAgents, agent]);
        }
      }
    }
    // Clear the select after adding
    setSelectedAgentOption(null);
  };

  const removeOffice = (officeId: string) => {
    setSelectedOffices(selectedOffices.filter(o => o.id !== officeId));
  };

  const removeAgent = (agentId: string) => {
    setSelectedAgents(selectedAgents.filter(a => a.id !== agentId));
  };

  // Helper function to fetch all team members
  const fetchAllTeamMembers = async () => {
    try {
      const allTeamMembers: TeamMember[] = [];

      // If offices are selected, fetch members via office-scoped select2 (paginated)
      if (selectedOffices.length > 0) {
        const officeIds = selectedOffices.map((o) => o.id).filter((id) => id && id !== "__ALL__");
        if (officeIds.length > 0) {
          let page = 1;
          let hasMore = true;
          while (hasMore) {
            const { results, hasMore: more } = await fetchOfficeTeamMembersSelect2Page(officeIds, page, "");
            results.forEach((item: any) => {
              const id = item.id || item._id;
              if (!id) return;
              const name = String(item.text ?? item.name ?? `${item.firstName || ""} ${item.lastName || ""}`.trim()).trim();
              if (!allTeamMembers.some((tm) => tm.id === id)) {
                allTeamMembers.push({
                  id,
                  name: name || "---",
                  firstName: item.firstName || "",
                  lastName: item.lastName || "",
                });
              }
            });
            hasMore = more;
            page++;
          }
        }
      } else {
        // Fallback to brokerage-wide team members
        let page = 1;
        let hasMore = true;
        while (hasMore) {
          const params = new URLSearchParams({
            page: String(page),
            limit: String(PAGE_SIZE)
          });
          if (selectedBrokerage) {
            params.append('brokerageFirmIDs[]', selectedBrokerage.value);
          }
          const url = `${process.env.REACT_APP_API_URL}/users/brokerage-team-members/by-brokerage-ids/select2?${params.toString()}`;
          const response = await fetch(url, {
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${token}`,
            },
          });
          if (!response.ok) break;
          const data = await response.json();
          const results = data.results || [];
          results.forEach((item: any) => {
            const id = item.id || item._id;
            if (!allTeamMembers.some(tm => tm.id === id)) {
              allTeamMembers.push({
                id,
                name: item.name,
                firstName: "",
                lastName: ""
              });
            }
          });
          hasMore = data.pagination?.more || false;
          page++;
        }
      }

      setSelectedTeamMembers(allTeamMembers);
    } catch (err) {
      console.error("Failed to fetch all team members:", err);
    }
  };

  const handleTeamMemberChange = (opt: TeamMemberOption | null) => {
    if (opt?.value) {
      if (opt.value === "__ALL__") {
        // Fetch and select all team members
        fetchAllTeamMembers();
      } else {
        const teamMember = { id: opt.value, name: opt.label, firstName: "", lastName: "" };
        if (!selectedTeamMembers.some(tm => tm.id === teamMember.id)) {
          setSelectedTeamMembers([...selectedTeamMembers, teamMember]);
        }
      }
    }
    // Clear the select after adding
    setSelectedTeamMemberOption(null);
  };

  const removeTeamMember = (teamMemberId: string) => {
    setSelectedTeamMembers(selectedTeamMembers.filter(tm => tm.id !== teamMemberId));
  };

  // Helper function to build filters
  const buildFilters = useCallback(() => {
    const filters: any = {
      payer_scope: scope,
      order_types: [],
      status: [],
      date_from: dateFrom ? `${dateFrom}T00:00:00Z` : undefined,
      date_to: dateTo ? `${dateTo}T23:59:59Z` : undefined,
      search: search.trim() || undefined
    };

    if (scope === "company" && brokerage?.id) {
      filters.buyer_organization_id = brokerage.id;
    }

    if (selectedOffices.length > 0) {
      filters.office_ids = selectedOffices.map(o => ({ id: o.id, name: o.label }));
    }

    if (selectedAgents.length > 0) {
      filters.agent_membership_ids = selectedAgents.map(a => ({
        membership_id: a.id,
        name: a.name,
      }));
    }

    if (selectedTeamMembers.length > 0) {
      filters.team_member_membership_ids = selectedTeamMembers.map(tm => ({
        membership_id: tm.id,
        name: tm.name,
      }));
    }

    if (scope === "group" && selectedGroup?.id) {
      filters.group_id = { id: selectedGroup.id, name: selectedGroup.name };
    }

    if (orderTypes.sales) filters.order_types.push("sales");
    if (orderTypes.service_install) filters.order_types.push("installation");
    if (orderTypes.removal) filters.order_types.push("removal");

    if (statusUnpaid) filters.status.push("Pending");
    if (statusPartial) filters.status.push("Partial Payment");
    if (statusOverdue) filters.status.push("Overdue");

    const billToCategoryMap: Record<PayerScope, string> = {
      company: "brokerage_firm",
      office: "office",
      agent: "agent",
      group: "group",
    };
    filters.bill_to_category = billToCategoryMap[scope];

    Object.keys(filters).forEach(key => {
      if (filters[key] === undefined || (Array.isArray(filters[key]) && filters[key].length === 0)) {
        delete filters[key];
      }
    });

    return filters;
  }, [
    scope,
    brokerage,
    selectedOffices,
    selectedAgents,
    selectedTeamMembers,
    selectedGroup,
    dateFrom,
    dateTo,
    search,
    orderTypes,
    statusUnpaid,
    statusPartial,
    statusOverdue
  ]);

  // ===== API: Get Orders List =====
  const fetchOrdersList = useCallback(async (overridePagination?: { pageIndex: number; pageSize: number }) => {
    try {
      setFetchingOrders(true);

      const filters = buildFilters();
      const paginationToUse = overridePagination || externalPagination || { pageIndex: 0, pageSize: 10 };
      const page = paginationToUse.pageIndex + 1;
      const limit = paginationToUse.pageSize;

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/batch-payments/orders/datatable`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            page,
            limit,
            sort_field: "order_date",
            sort_dir: -1,
            filters
          })
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch orders");
      }

      const data = await response.json();
      console.log("Fetched orders data:", data);
      const orders = data?.data || [];
      const total = data?.kpis?.orders || 0;
      const totalPages = data?.totalPages || Math.ceil(total / limit);

      onOrdersFetched?.({
        orders,
        total,
        totalPages,
        page,
        limit
      });

      setOrdersFetched(true);

    } catch (err) {
      showError(err instanceof Error ? err.message : "Failed to fetch orders");
    } finally {
      setFetchingOrders(false);
    }
  }, [
    buildFilters,
    externalPagination,
    token,
    onOrdersFetched
  ]);

  // Ref to track previous pagination to detect changes
  const prevPaginationRef = useRef<{ pageIndex: number; pageSize: number } | null>(null);

  // Refetch orders when pagination changes (only if orders were already fetched)
  useEffect(() => {
    if (ordersFetched && externalPagination) {
      const prevPagination = prevPaginationRef.current;
      // Only refetch if pagination actually changed
      if (!prevPagination || 
          prevPagination.pageIndex !== externalPagination.pageIndex || 
          prevPagination.pageSize !== externalPagination.pageSize) {
        prevPaginationRef.current = externalPagination;
        fetchOrdersList();
      }
    }
  }, [externalPagination?.pageIndex, externalPagination?.pageSize, ordersFetched, fetchOrdersList]);

  // Reset pagination ref when orders are first fetched
  useEffect(() => {
    if (ordersFetched && externalPagination) {
      prevPaginationRef.current = externalPagination;
    }
  }, [ordersFetched]);

  // ===== API: Generate Report =====
  const generateReport = async () => {
    try {
      setGeneratingReport(true);

      const billToCategoryMap: Record<PayerScope, string> = {
        company: "brokerage_firm",
        office: "office",
        agent: "agent",
        group: "group",
      };

      const filters: any = {
        payer_scope: scope,
        status: [],
        order_types: [],
        date_from: dateFrom ? `${dateFrom}T00:00:00Z` : undefined,
        date_to: dateTo ? `${dateTo}T23:59:59Z` : undefined,
        search: search.trim() || undefined,
        bill_to_category: billToCategoryMap[scope],
      };

      if (brokerage?.id) {
        filters.company_id = brokerage.id;
      }

      if (selectedOffices.length > 0) {
        filters.office_ids = selectedOffices.map(o => ({ id: o.id, name: o.label }));
      }

      if (selectedAgents.length > 0) {
        filters.agent_ids = selectedAgents.map(a => ({ id: a.id, name: a.name }));
      }

      if (selectedTeamMembers.length > 0) {
        filters.team_member_ids = selectedTeamMembers.map(tm => ({ id: tm.id, name: tm.name }));
      }

      if (statusUnpaid) filters.status.push("Pending");
      if (statusPartial) filters.status.push("Partial Payment");
      if (statusOverdue) filters.status.push("Overdue");

      if (orderTypes.sales) filters.order_types.push("sales");
      if (orderTypes.service_install) filters.order_types.push("installation");
      if (orderTypes.removal) filters.order_types.push("removal");

      const selectedOrders = (selectedOrdersForReport || [])
        .map((o) => ({
          order_id: String(o.order_id || "").trim(),
          order_type: String(o.order_type || "sales").toLowerCase(),
        }))
        .filter((o) => o.order_id);
      if (selectedOrders.length > 0) {
        filters.selected_orders = selectedOrders;
      }

      Object.keys(filters).forEach((key) => {
        const value = filters[key];
        if (
          value === undefined ||
          value === "" ||
          (Array.isArray(value) && value.length === 0)
        ) {
          delete filters[key];
        }
      });

      const billToMembers: any[] = [];
      selectedAgents.forEach((agent) =>
        billToMembers.push({ id: agent.id, type: "agent", name: agent.name })
      );
      selectedTeamMembers.forEach((teamMember) =>
        billToMembers.push({ id: teamMember.id, type: "team_member", name: teamMember.name })
      );
      selectedOffices.forEach((office) =>
        billToMembers.push({ id: office.id, type: "office", name: office.label })
      );
      if (scope === "group" && selectedGroup) {
        billToMembers.push({
          id: selectedGroup.id,
          type: "group",
          name: selectedGroup.name,
        });
      }

      const billMeta = {
        bill_from_name: vendorName || "Signage",
        bill_to_name: selectedBrokerage?.label || "",
        bill_to_members: billToMembers,
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/batch-payments/reports`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            filters,
            bill_meta: billMeta
          })
        }
      );

      if (!response.ok) {
        throw new Error("Failed to generate report");
      }

      const data = await response.json();
      console.log("Generated report data:", data);

      // Navigate to report page with the _id
      navigate(`${baseRoute}/batchPaymentReport/${data._id}`);
      
      showSuccess("Report generated successfully!");

    } catch (err) {
      showError(err instanceof Error ? err.message : "Failed to generate report");
    } finally {
      setGeneratingReport(false);
    }
  };

  // Removed companyOptions - now using AsyncPaginate
  const teamMemberOptions = useMemo(() => teamMembers.map((tm) => ({ value: tm.id, label: tm.name })), [teamMembers]);
  const officeOptions = useMemo(() => offices.map((o) => ({ value: o.id, label: o.label })), [offices]);
  const agentOptions = useMemo(() => {
    const options = agents.map((a) => ({ value: a.id, label: a.name }));
    // Add "All" option at the top
    return [{ value: "__ALL__", label: "All" }, ...options];
  }, [agents]);
  const groupOptions = useMemo(() => groups.map((g) => ({ value: g.id, label: g.name })), [groups]);

  return (
    <div className="card shadow-border-1 box-shadow-datatables p-2 bg-white mb-3">
      <div className="card-body">
        <div className="fw-semibold mb-3">Payer Scope</div>

        {/* Scope Radios */}
        <div className="d-flex flex-wrap gap-4 border-bottom pb-4">
          {["company", "office", "agent", "group"].map((k) => (
            <label key={k} className="form-check d-inline-flex align-items-center gap-2">
              <input type="radio" className="form-check-input" checked={scope === (k as PayerScope)} onChange={() => handleScopeChange(k as PayerScope)} />
              <span className="text-capitalize">{k}</span>
            </label>
          ))}
          {scope === "group" && (
            <div style={{ minWidth: 220 }}>
              <Select
                classNamePrefix="rs"
                placeholder={loading ? "Loading groups..." : "Select group..."}
                options={groupOptions}
                value={selectedGroup ? { value: selectedGroup.id, label: selectedGroup.name } : null}
                onChange={(opt: any) => setSelectedGroup(opt ? { id: opt.value, name: opt.label } : null)}
                components={{ DropdownIndicator }}
                styles={customSelectStyles}
              />
            </div>
          )}
        </div>

        {/* Company - Only show for Company scope */}
        {scope === "company" && (
          <>
            <div className="row g-3 align-items-center mt-2">
              <div className="col-lg-3">
                <label className="col-form-label">Company</label>
                <AsyncPaginate
                  classNamePrefix="rs"
                  placeholder="Search companies..."
                  value={selectedBrokerage}
                  loadOptions={loadBrokerageOptions}
                  onChange={(opt: BrokerageOption | null) => {
                    setSelectedBrokerage(opt);
                    setBrokerage(opt ? { id: opt.value, name: opt.label } : null);
                    // Clear dependent selections when company changes
                    setSelectedOffices([]);
                    setSelectedOffice(null);
                    setSelectedOfficeOption(null);
                    setSelectedAgents([]);
                    setSelectedAgent(null);
                    setSelectedAgentOption(null);
                    setSelectedTeamMembers([]);
                    setSelectedTeamMember(null);
                    setSelectedTeamMemberOption(null);
                  }}
                  additional={{ page: 1 }}
                  defaultOptions
                  debounceTimeout={300}
                  isClearable
                  components={{ DropdownIndicator }}
                  styles={customSelectStyles}
                />
              </div>
            </div>
          </>
        )}

        {/* Office - Only show for Office scope */}
        {scope === "office" && (
          <div className="row g-3 align-items-center mt-2">
            <div className="col-lg-3">
              <label className="col-form-label">Company</label>
              <AsyncPaginate
                classNamePrefix="rs"
                placeholder="Search companies..."
                value={selectedBrokerage}
                loadOptions={loadBrokerageOptions}
                onChange={(opt: BrokerageOption | null) => {
                  setSelectedBrokerage(opt);
                  setBrokerage(opt ? { id: opt.value, name: opt.label } : null);
                  // Clear dependent selections when company changes
                  setSelectedOffices([]);
                  setSelectedOffice(null);
                  setSelectedOfficeOption(null);
                  setSelectedAgents([]);
                  setSelectedAgent(null);
                  setSelectedAgentOption(null);
                  setSelectedTeamMembers([]);
                  setSelectedTeamMember(null);
                  setSelectedTeamMemberOption(null);
                }}
                additional={{ page: 1 }}
                defaultOptions
                debounceTimeout={300}
                isClearable
                components={{ DropdownIndicator }}
                styles={customSelectStyles}
              />
            </div>
            {brokerage && (
              <>
                <div className="row mt-2">
                  <div className="col-lg-3">
                    <label className="col-form-label">Office</label>
                    <AsyncPaginate
                      key={selectedBrokerage?.value || 'no-brokerage'}
                      classNamePrefix="rs"
                      placeholder="Select office..."
                      isDisabled={!selectedBrokerage}
                      loadOptions={loadOfficeOptions}
                      value={selectedOfficeOption}
                      onChange={handleOfficeChange}
                      additional={{ page: 1 }}
                      defaultOptions={true}
                      debounceTimeout={300}
                      isClearable
                      components={{ DropdownIndicator }}
                      styles={customSelectStyles}
                    />
                    {/* Chips under the select */}
                    {selectedOffices.length > 0 && (
                      <div className="d-flex gap-2 flex-wrap mt-2">
                        {selectedOffices.map((o) => (
                          <span key={o.id} className="chip badge rounded-pill fs-11 fw-medium">
                            {o.label}
                            <span className="ms-1 fs-16" onClick={() => removeOffice(o.id)}>×</span>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                {/* Team Members selector hidden for Office scope by request. */}
                {false && (
                <div className="row mt-2">
                  <div className="col-lg-3">
                    <div className="col-form-label">Team Members</div>
                    <AsyncPaginate
                      key={selectedOffices.map(o => o.id).join(',') || 'no-offices'}
                      classNamePrefix="rs"
                      placeholder="Select team member..."
                      isDisabled={!selectedOffices.length}
                      loadOptions={loadTeamMemberOptions}
                      value={selectedTeamMemberOption}
                      onChange={handleTeamMemberChange}
                      additional={{ page: 1 }}
                      defaultOptions={true}
                      debounceTimeout={300}
                      isClearable
                      components={{ DropdownIndicator }}
                      styles={customSelectStyles}
                    />
                    {/* Chips under the select */}
                    {selectedTeamMembers.length > 0 && (
                      <div className="d-flex gap-2 flex-wrap mt-2">
                        {selectedTeamMembers.map((tm) => (
                          <span key={tm.id} className="chip badge rounded-pill fs-11 fw-medium">
                            {tm.name}
                            <span className="ms-1 fs-16" onClick={() => removeTeamMember(tm.id)}>×</span>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                )}
                <div className="row mt-2">
                  <div className="col-lg-3">
                    <label className="col-form-label">Agent</label>
                    <Select
                      classNamePrefix="rs"
                      placeholder="Select agent..."
                      options={agentOptions}
                      value={selectedAgent}
                      onChange={handleAgentChange}
                      components={{ DropdownIndicator }}
                      styles={customSelectStyles}
                    />
                    {/* Chips under the select */}
                    {selectedAgents.length > 0 && (
                      <div className="d-flex gap-2 flex-wrap mt-2">
                        {selectedAgents.map((a) => (
                          <span key={a.id} className="chip badge rounded-pill fs-11 fw-medium">
                            {a.name}
                            <span className="ms-1 fs-16" onClick={() => removeAgent(a.id)}>×</span>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Agent - Only show for Agent scope */}
        {scope === "agent" && (
          <div className="row g-3 align-items-center mt-2">
            <div className="col-lg-3">
              <label className="col-form-label">Agent</label>
              <AsyncPaginate
                key={`signage-agents-${organizationId}`}
                classNamePrefix="rs"
                placeholder="Search agents..."
                value={selectedAgentOption}
                loadOptions={loadSignageAgentOptions}
                onChange={handleAgentChange}
                additional={{ page: 1 }}
                defaultOptions
                debounceTimeout={300}
                isClearable
                isDisabled={!organizationId}
                components={{ DropdownIndicator }}
                styles={customSelectStyles}
              />
              {/* Chips under the select */}
              {selectedAgents.length > 0 && (
                <div className="d-flex gap-2 flex-wrap mt-2">
                  {selectedAgents.map((a) => (
                    <span key={a.id} className="chip badge rounded-pill fs-11 fw-medium">
                      {a.name}
                      <span className="ms-1 fs-16" onClick={() => removeAgent(a.id)}>×</span>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Bill-To Category - Dynamic label based on scope */}
        {scope !== "group" && (
          <div className={`row g-3 align-items-center mt-2 ${scope === "company" ? "" : "border-bottom pb-4"}`}>
            <div className="col-lg-2">
              <label className="col-form-label">Bill-To Category</label>
              <div className="form-check">
                <input id="billToCategory" className="form-check-input" type="checkbox" checked={billToCompany} onChange={(e) => setBillToCompany(e.target.checked)} disabled />
                <label htmlFor="billToCategory" className="form-check-label">
                  {scope === "company" && "Company-billed"}
                  {scope === "office" && "Office-billed"}
                  {scope === "agent" && "Agent-billed"}
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Refine (optional) - Only show for Company scope */}
        {scope === "company" && (
          <>
            <div className="row mt-1">
              <div className="col-lg-12">
                <label className="col-form-label">Refine (optional)</label>
              </div>
            </div>

            {/* Refine */}
            <div className="row g-4 align-items-start border-bottom pb-4">
            <div className="col-lg-3">
                <div className="mb-2 fw-semibold">Offices</div>
                <AsyncPaginate
                  key={selectedBrokerage?.value || 'no-brokerage'}
                  classNamePrefix="rs"
                  placeholder="Select office..."
                  isDisabled={!selectedBrokerage}
                  loadOptions={loadOfficeOptions}
                  value={selectedOfficeOption}
                  onChange={handleOfficeChange}
                  additional={{ page: 1 }}
                  defaultOptions={true}
                  debounceTimeout={300}
                  isClearable
                  components={{ DropdownIndicator }}
                  styles={customSelectStyles}
                />
                {selectedOffices.length > 0 && (
                  <div className="d-flex gap-2 flex-wrap mt-2">
                    {selectedOffices.map((o) => (
                      <span key={o.id} className="chip badge rounded-pill fs-11 fw-medium">
                        {o.label}
                        <span className="ms-1 fs-16" onClick={() => removeOffice(o.id)}>×</span>
                      </span>
                    ))}
                  </div>
                )}

                <div className="mt-2 fs-13">Company-billed = payer is the company. Use Refine to include only certain offices/agents.</div>
              </div>
              <div className="col-lg-3">
                <div className="mb-2 fw-semibold">Team Members</div>
                <AsyncPaginate
                  key={selectedOffices.map(o => o.id).join(',') || 'no-offices'}
                  classNamePrefix="rs"
                  placeholder="Select team member..."
                  isDisabled={selectedOffices.length === 0}
                  loadOptions={loadTeamMemberOptions}
                  value={selectedTeamMemberOption}
                  onChange={handleTeamMemberChange}
                  additional={{ page: 1 }}
                  defaultOptions={true}
                  debounceTimeout={300}
                  isClearable
                  components={{ DropdownIndicator }}
                  styles={customSelectStyles}
                />
                {/* Chips under the select */}
                {selectedTeamMembers.length > 0 && (
                  <div className="d-flex gap-2 flex-wrap mt-2">
                    {selectedTeamMembers.map((tm) => (
                      <span key={tm.id} className="chip badge rounded-pill fs-11 fw-medium">
                        {tm.name}
                        <span className="ms-1 fs-16" onClick={() => removeTeamMember(tm.id)}>×</span>
                      </span>
                    ))}
                  </div>
                )}
                
              </div>
              {/* Agents selector hidden for Company scope by request. */}
              {false && (
              <div className="col-lg-3">
                <div className="mb-2 fw-semibold">Agents</div>
                <AsyncPaginate
                  key={selectedOffices.map(o => o.id).join(',') || 'no-offices'}
                  classNamePrefix="rs"
                  placeholder="Select agent..."
                  isDisabled={selectedOffices.length === 0}
                  loadOptions={loadAgentOptions}
                  value={selectedAgentOption}
                  onChange={handleAgentChange}
                  additional={{ page: 1 }}
                  defaultOptions={true}
                  debounceTimeout={300}
                  isClearable
                  components={{ DropdownIndicator }}
                  styles={customSelectStyles}
                />
                {selectedAgents.length > 0 && (
                  <div className="d-flex gap-2 flex-wrap mt-2">
                    {selectedAgents.map((a) => (
                      <span key={a.id} className="chip badge rounded-pill fs-11 fw-medium">
                        {a.name}
                        <span className="ms-1 fs-16" onClick={() => removeAgent(a.id)}>×</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              )}
            </div>
          </>
        )}

        {/* Placed By / Types / Dates */}
        <div className="row g-4 mt-2 border-bottom pb-4">
          {/* <div className="col-lg-4">
            <div className="mb-2 fw-semibold">Placed By</div>
            <label className="form-check d-flex align-items-center gap-2">
              <input type="checkbox" className="form-check-input" checked={placedBy.officeManager} onChange={(e) => setPlacedBy({ ...placedBy, officeManager: e.target.checked })} />
              Office Manager
            </label>
            <label className="form-check d-flex align-items-center gap-2 mt-2">
              <input type="checkbox" className="form-check-input" checked={placedBy.agent} onChange={(e) => setPlacedBy({ ...placedBy, agent: e.target.checked })} />
              Agent
            </label>
          </div> */}
          <div className="col-lg-4">
            <div className="mb-2 fw-semibold">Order Types</div>
            <label className="form-check d-flex align-items-center gap-2">
              <input type="checkbox" className="form-check-input" checked={orderTypes.sales} onChange={(e) => setOrderTypes({ ...orderTypes, sales: e.target.checked })} />
              Sales
            </label>
            <label className="form-check d-flex align-items-center gap-2 mt-2">
              <input type="checkbox" className="form-check-input" checked={orderTypes.service_install} onChange={(e) => setOrderTypes({ ...orderTypes, service_install: e.target.checked })} />
              Installation
            </label>
            <label className="form-check d-flex align-items-center gap-2 mt-2">
              <input type="checkbox" className="form-check-input" checked={orderTypes.removal} onChange={(e) => setOrderTypes({ ...orderTypes, removal: e.target.checked })} />
              Removal
            </label>
          </div>
          <div className="col-lg-4">
            <div className="mb-2 fw-semibold">Date Range</div>
            <div className="d-flex gap-2">
              <div className="">
                <div className="fw-medium mb-1">From</div>
                <input type="date" className="payer-form-control" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
              </div>
              <div className="">
                <div className="fw-medium mb-1">To</div>
                <input type="date" className="payer-form-control" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
              </div>
            </div>
          </div>
        </div>

        {/* Status + Actions */}
        <div className="mt-4 d-flex align-items-center gap-2 flex-wrap border-bottom pb-4">
          <div className="fw-semibold me-2">Status:</div>
          <button
            className={`btn btn-sm btn-unpaid ${statusUnpaid ? "active" : "outline"} fw-bolder br-8 fs-12`}
            onClick={() => setStatusUnpaid(!statusUnpaid)}
          >
            Unpaid {statusUnpaid ? "(ON)" : "(OFF)"}
          </button>
          <button
            className={`btn btn-sm btn-partial ${statusPartial ? "active" : "outline"} fw-bolder br-8 fs-12`}
            onClick={() => setStatusPartial(!statusPartial)}
          >
            Partially Paid {statusPartial ? "(ON)" : "(OFF)"}
          </button>
          <button
            className={`btn btn-sm btn-overdue ${statusOverdue ? "active" : "outline"} fw-bolder br-8 fs-12`}
            onClick={() => setStatusOverdue(!statusOverdue)}
          >
            Overdue {statusOverdue ? "(ON)" : "(OFF)"}
          </button>
        </div>
        <div className="row mt-4">
          <div className="col-12 col-lg-5">
            <div className="d-flex gap-3 align-items-center">
              <div className="search-container flex-grow-1">
                <div className="search-input-wrapper">
                  <Search size={16} className="search-icon" />
                  <input
                    className="payer-form-control"
                    placeholder="Search orders..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        fetchOrdersList();
                      }
                    }}
                  />
                </div>
              </div>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-primary table-addbutton btn-slim fw-bold"
                  onClick={() => {
                    // Reset pagination to first page when clicking "Get Orders List"
                    if (onPaginationReset) {
                      onPaginationReset();
                    }
                    // Fetch with page 0 explicitly
                    fetchOrdersList({ pageIndex: 0, pageSize: 10 });
                  }}
                  disabled={fetchingOrders}
                >
                  <Search size={16} className="me-2" />
                  {fetchingOrders ? "Loading..." : "Get Orders List"}
                </button>
                {ordersFetched && (
                  <button
                    className="btn success-btn btn-slim fw-bold"
                    onClick={generateReport}
                    disabled={generatingReport}
                  >
                    <FileText size={16} className="me-2" />
                    {generatingReport ? "Generating..." : "Generate Batch Invoice"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        /* Chip badges - gray by default, colored on hover */
        .chip{
            background:#f5f5f5;
            color:#333;
            border:1px solid #e0e0e0;
            padding:.35rem .6rem;
            cursor:default;
            transition:all 0.2s ease;
        }
        .chip:hover{
            background:#ffeaea;
            border-color:#ffd0d0;
        }

        /* Custom status buttons per spec */
        .btn-unpaid.active{background:#ff6900;color:#fff;border-color:#ff6900}
        .btn-unpaid.active:hover{background:#f54a00;border-color:#f54a00}
        .btn-unpaid.outline{background:transparent;color:#ff6900;border:1px solid #ff6900}
        .btn-unpaid.outline:hover{background:#ffe2e2}

        .btn-partial.active{background:#ad46ff;color:#fff;border-color:#ad46ff}
        .btn-partial.active:hover{background:#9810fa;border-color:#9810fa}
        .btn-partial.outline{background:transparent;color:#ad46ff;border:1px solid #ad46ff}
        .btn-partial.outline:hover{background:#f3e6ff}

        .btn-overdue.active{background:#f53298;color:#fff;border-color:#f472b6}
        .btn-overdue.active:hover{background:#d8006b;border-color:#ec4899}
        .btn-overdue.outline{background:transparent;color:#ec4899;border:1px solid #ec4899}
        .btn-overdue.outline:hover{background:#fce7f3}

        /* Keep select look tidy */
        .rs__control{min-height:44px;border-radius:8px}

        /* Payer form controls - same styling as ups-form-control */
        .payer-form-control {
          width: 100%; 
          height: 39px; 
          border-radius: 10px; 
          padding: 10px 12px;
          border-width: 2px; 
          border-style: solid; 
          border-color: #c9ced6; 
          background-color: #f5f6fa;
          outline: none; 
          transition: all .2s ease-in-out; 
          box-shadow: none; 
          color: #111827;
        }
        .payer-form-control:hover { 
          border-color: #9ca3af; 
          background-color: #fafafa; 
        }
        .payer-form-control:focus { 
          border-color: #9ca3af; 
          background-color: #fafafa; 
          box-shadow: none; 
        }
        .payer-form-control.is-invalid { 
          border-color: #dc3545; 
        }

        /* Slim search bar and buttons */
        .search-container {
            position: relative;
        }
        
        .search-input-wrapper {
            position: relative;
            display: flex;
            align-items: center;
        }
        
        .search-icon {
            position: absolute;
            left: 12px;
            color: #6c757d;
            z-index: 2;
        }
        
        /* Override payer-form-control for search input with icon */
        .search-input-wrapper .payer-form-control {
            padding-left: 40px;
        }
        
        .btn-slim {
            height: 40px;
            padding: 8px 16px;
            font-size: 14px;
            font-weight: 500;
            border-radius: 8px;
            display: flex;
            align-items: center;
            white-space: nowrap;
            transition: all 0.2s ease;
        }
              `}</style>
    </div>
  );
}